import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { orderBy, sortBy } from "lodash";
import { Row, Col } from "react-bootstrap";
import CustomerSelect from "../../common/CustomerSelect";
import ProjectSelect from "../../common/ProjectSelect";
import CallTypeSelect from "../../common/CallTypeSelect";
import DropdownSearch from "../../common/DropdownSearch";
import DepartmentSelect from "../../common/DepartmentSelect";
import UserSelect from "../../common/UserSelect";
import ServiceSelect from "../../common/ServiceSelect";

import ReportFilters from "../ReportFiltersComponent";

const TaskDetailFilters = ({
  onChange,
  codes,
  filters,
  onResetClick,
  onFetchClick,
  loading,
  disabled,
  exportDisabled,
  onCSVExport,
  onPDFExport,
  service,
  auth
}) => {
  const generateCodeOptions = (entity, field) => {
    const codeOptions = codes
      ?.filter((code) => code.entity === entity && code.field === field)
      ?.map((code) => ({ value: code._id, label: code.label }));

    return orderBy(codeOptions, [(option) => option.label.toLowerCase()]);
  };

  const tasksAvailable = useMemo(() => {
    const taskOptions = service?.tasks?.map(({ task, id }) => ({
      value: id,
      label: task.description
    }));
    return sortBy(taskOptions, "label");
  }, [service]);

  return (
    <ReportFilters
      onFetchClick={() => onFetchClick()}
      onResetClick={onResetClick}
      title="Task Detail"
      loading={loading}
      disabled={disabled}
      csvExportAvailable
      pdfExportAvailable
      exportDisabled={exportDisabled}
      onCSVExport={onCSVExport}
      onPDFExport={onPDFExport}
    >
      <Row>
        <Col md={3}>
          <CustomerSelect
            isMulti
            label="Customer"
            placeholder="Select"
            value={filters.filtered.customer || null}
            onChange={(e) => onChange("customer")(e || [])}
          />
        </Col>
        <Col md={3}>
          <ProjectSelect
            isMulti
            label="Project"
            placeholder="Select"
            value={filters.filtered.project || null}
            onChange={(e) => onChange("project")(e || [])}
            selectedCustomers={
              filters.filtered.customer ? filters.filtered.customer : null
            }
          />
        </Col>
        <Col md={3}>
          <CallTypeSelect
            label="Call Type"
            placeholder="Select"
            value={filters.filtered.call_type || null}
            onChange={(e) => onChange("call_type")(e || null)}
          />
        </Col>
        <Col md={3}>
          <DepartmentSelect
            isMulti
            label="Department"
            placeholder="Select"
            value={filters.filtered.department || null}
            onChange={(e) => onChange("department")(e || [])}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 0 }}>
        <Col md={3}>
          <UserSelect
            label="Assigned To"
            placeholder="Select"
            value={filters.filtered.assigned_to || null}
            onChange={(e) => onChange("assigned_to")(e || null)}
          />
        </Col>
        <Col md={3}>
          <UserSelect
            label="Project Manager"
            placeholder="Select"
            value={filters.filtered.project_manager || null}
            onChange={(e) => onChange("project_manager")(e || null)}
          />
        </Col>
        <Col md={3}>
          <ServiceSelect
            label="Service"
            placeholder="Select"
            value={filters.filtered.service || null}
            onChange={(e) => onChange("service")(e || null)}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            tooltipId="service-task-select"
            tooltipClassName={`custom-tooltip ${
              auth.isNightMode ? "dark" : "light"
            }`}
            dataTooltipVariant={auth.isNightMode ? "dark" : "light"}
            className="disabled-tooltip"
            isClearable
            rowStyles={{ margin: 0 }}
            disabled={!filters.filtered.service}
            name="service_task"
            label="Task"
            onChange={(name, value) => onChange("service_task")(value || null)}
            data={tasksAvailable}
            value={filters.filtered.service_task?.value}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: 15 }}>
        <Col md={3}>
          <DropdownSearch
            rowClassNames=""
            rowStyles={{ marginLeft: 0, marginRight: 0, marginTop: 0 }}
            isMulti
            label="Call Status"
            placeholder="Select"
            name="stage"
            onChange={(name, value) => onChange("stage")(value || [])}
            value={filters.filtered.stage || null}
            data={[...generateCodeOptions("Call", "status")]}
          />
        </Col>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            Start Date *
          </label>
          <input
            style={{
              padding: 8,
              border: "1px solid hsl(0,0%,80%)",
              width: "100%",
              borderRadius: 4
            }}
            name="startDate"
            type="date"
            value={filters.filtered.startDate}
            onChange={(e) => onChange("startDate")(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            End Date *
          </label>
          <input
            style={{
              padding: 8,
              border: "1px solid hsl(0,0%,80%)",
              width: "100%",
              borderRadius: 4
            }}
            name="endDate"
            type="date"
            value={filters.filtered.endDate}
            onChange={(e) => onChange("endDate")(e.target.value)}
          />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            * Required Fields
          </label>
        </Col>
      </Row>
    </ReportFilters>
  );
};

TaskDetailFilters.propTypes = {
  onChange: PropTypes.func.isRequired,
  onResetClick: PropTypes.func,
  onFetchClick: PropTypes.func,
  codes: PropTypes.array,
  filters: PropTypes.object,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  exportDisabled: PropTypes.func,
  onCSVExport: PropTypes.func,
  onPDFExport: PropTypes.func,
  service: PropTypes.object,
  auth: PropTypes.object
};

TaskDetailFilters.defaultProps = {
  service: {
    tasks: []
  }
};

export default TaskDetailFilters;
